import React, { useMemo, useState } from 'react';
import { Button, List, Popover, Typography } from 'antd';
import { Eye } from 'lucide-react';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import UserDetailsDrawer from './UserDetailsDrawer';

const { Text } = Typography;

export function resolveOnlineUsers(onlineUserIds, users) {
  const byId = new Map((users || []).map((u) => [Number(u.id), u.username]));
  return (onlineUserIds || [])
    .map((id) => {
      const numId = Number(id);
      return {
        id: numId,
        username: byId.get(numId) || `Пользователь #${numId}`,
      };
    })
    .sort((a, b) => a.username.localeCompare(b.username, 'ru'));
}

function OnlineUsersList({ onlineUsers, onViewProfile }) {
  if (!onlineUsers.length) {
    return <Text type="secondary">Никого нет в сервисе</Text>;
  }

  return (
    <List
      size="small"
      split={false}
      dataSource={onlineUsers}
      renderItem={(user) => (
        <List.Item
          className="admin-online-users-list__item"
          actions={[
            <Button
              key="view"
              type="text"
              size="small"
              className="admin-online-users-list__view"
              icon={<Eye size={16} />}
              aria-label={`Профиль ${user.username}`}
              title="Открыть профиль"
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile?.(user.id);
              }}
            />,
          ]}
        >
          <span className="admin-online-users-list__name">{user.username}</span>
        </List.Item>
      )}
    />
  );
}

export function AdminOnlinePopover({ onlineUserIds, users, children, placement = 'bottomLeft' }) {
  const { getUser } = useAdminSocketContext();
  const [profileUserId, setProfileUserId] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const onlineUsers = useMemo(
    () => resolveOnlineUsers(onlineUserIds, users),
    [onlineUserIds, users]
  );
  const count = onlineUsers.length;
  const onlineSet = useMemo(() => new Set((onlineUserIds || []).map(Number)), [onlineUserIds]);

  const handleViewProfile = (userId) => {
    setPopoverOpen(false);
    setProfileUserId(userId);
  };

  if (!children) return null;

  return (
    <>
      <Popover
        title={count ? `В сервисе (${count})` : 'В сервисе'}
        content={
          <OnlineUsersList onlineUsers={onlineUsers} onViewProfile={handleViewProfile} />
        }
        trigger={['hover', 'click']}
        placement={placement}
        mouseEnterDelay={0.2}
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
      >
        {children}
      </Popover>

      <UserDetailsDrawer
        open={profileUserId != null}
        userId={profileUserId}
        onClose={() => setProfileUserId(null)}
        getUser={getUser}
        isOnline={profileUserId != null && onlineSet.has(Number(profileUserId))}
      />
    </>
  );
}

const AdminOnlineIndicator = ({ onlineUserIds, users, className = '' }) => {
  const count = onlineUserIds?.length || 0;

  const badge = (
    <span
      className={`admin-online-badge admin-online-badge--interactive${className ? ` ${className}` : ''}`}
      role="button"
      tabIndex={0}
    >
      Онлайн: {count}
    </span>
  );

  return (
    <AdminOnlinePopover onlineUserIds={onlineUserIds} users={users}>
      {badge}
    </AdminOnlinePopover>
  );
};

export default AdminOnlineIndicator;
